import { api } from './api';

/**
 * Build workspace filter params
 * @param {Object} filterProps - Filter props from WorkspaceContext
 * @returns {Object} Query parameters for workspace filtering
 */
const buildWorkspaceParams = (filterProps = {}) => {
  const params = {};
  if (filterProps.organizationId) {
    params.organizationId = filterProps.organizationId;
  }
  if (filterProps.teamId) {
    params.teamId = filterProps.teamId;
  }
  return params;
};

/**
 * Get items by estimate value for calibration gallery
 * @param {string} estimate - The estimate value to filter by
 * @param {Object} options - Additional options (search, limit, offset, organizationId, teamId)
 */
export const getCalibrationItems = (estimate, options = {}) => {
  const { organizationId, teamId, ...otherOptions } = options;
  return api.get('/analytics/calibration/items', {
    params: {
      estimate,
      ...otherOptions,
      ...buildWorkspaceParams({ organizationId, teamId })
    }
  });
};

/**
 * Get summary of item counts per estimate value
 * @param {Object} filterProps - Optional workspace filter props (organizationId, teamId)
 */
export const getCalibrationSummary = (filterProps = {}) => {
  return api.get('/analytics/calibration/summary', {
    params: buildWorkspaceParams(filterProps)
  });
};

/**
 * Get team consensus patterns by estimate value
 * @param {Object} options - Optional filters (sessionId, organizationId, teamId)
 */
export const getTeamPatterns = (options = {}) => {
  return api.get('/analytics/patterns/team', { params: options });
};

/**
 * Get individual participant voting tendencies
 * @param {Object} options - Optional filters (sessionId, organizationId, teamId)
 */
export const getIndividualTendencies = (options = {}) => {
  return api.get('/analytics/patterns/individual', { params: options });
};

/**
 * Get point drift data for items estimated multiple times
 * @param {Object} options - Optional filters (jiraKey, organizationId, teamId)
 */
export const getPointDrift = (options = {}) => {
  return api.get('/analytics/trends/drift', { params: options });
};

/**
 * Get previous items with the same estimate (for inline session panel)
 * @param {string} estimate - The estimate value to match
 * @param {string} excludeSessionId - Session ID to exclude from results
 * @param {number} limit - Maximum number of items to return (default 5)
 */
export const getPreviousEstimates = (estimate, excludeSessionId, limit = 5) => {
  return api.get('/analytics/reference/previous', {
    params: { estimate, excludeSessionId, limit }
  });
};

/**
 * Find previous estimates for a specific Jira issue
 * @param {string} jiraKey - The Jira issue key (e.g., "PROJ-123")
 */
export const getJiraEstimateHistory = (jiraKey) => {
  return api.get(`/analytics/match/jira/${encodeURIComponent(jiraKey)}`);
};

/**
 * Get estimation accuracy trend over sessions
 * @param {Object} options - Optional filters (organizationId, teamId, scope, limit)
 */
export const getAccuracyTrend = (options = {}) => {
  return api.get('/analytics/accuracy/trend', { params: options });
};

/**
 * Get accuracy breakdown by estimate value
 * @param {Object} options - Optional filters (organizationId, teamId, scope)
 */
export const getAccuracyByEstimate = (options = {}) => {
  return api.get('/analytics/accuracy/by-estimate', { params: options });
};

/**
 * Get overall estimation accuracy summary
 * @param {Object} options - Optional filters (organizationId, teamId, scope)
 */
export const getAccuracySummary = (options = {}) => {
  return api.get('/analytics/accuracy/summary', { params: options });
};

const analyticsApi = {
  getCalibrationItems,
  getCalibrationSummary,
  getTeamPatterns,
  getIndividualTendencies,
  getPointDrift,
  getPreviousEstimates,
  getJiraEstimateHistory,
  getAccuracyTrend,
  getAccuracyByEstimate,
  getAccuracySummary
};

export default analyticsApi;
